import React from 'react';
import { Card, Row, Col, Typography, Statistic } from 'antd';
import { useSelector } from 'react-redux';
import { formatIndicatorValueForChart } from '../../../../core/utils/helpers';
import { TimeSeriesComparison, SchoolTimeSeriesIndicator, SpecialIndicatorTimeSeries } from '../../../../core/indicator/types';
import { CombinedStates } from '../../../../core/types';

const { Title, Text } = Typography;

// Custom Line Chart Component
interface LineChartProps {
    data: Array<{
        period: string;
        periodDescription: string;
        school: string;
        value: number;
        displayValue: string;
        confirmed: boolean;
        updatedAt: string;
    }>;
    schools: string[];
    getSchoolColor: (school: string) => string;
    height?: number;
}

const LineChart: React.FC<LineChartProps> = ({ data, schools, getSchoolColor, height = 450 }) => {
    if (data.length === 0) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">Nėra duomenų grafike</Text>
            </div>
        );
    }

    // Get all unique periods and sort them chronologically
    const periods = [...new Set(data.map(d => d.period))].sort((a, b) => {
        // Helper function to convert period to sortable format
        const getSortablePeriod = (period: string) => {
            // Handle school year format (2022-2023)
            if (period.includes('-') && !period.startsWith('Q')) {
                const [startYear] = period.split('-');
                return parseInt(startYear || '0', 10);
            }

            // Handle quarter format (Q3-2023)
            if (period.startsWith('Q')) {
                const [, year] = period.split('-');
                const quarter = parseInt(period.charAt(1), 10);
                return parseInt(year || '0', 10) + (quarter - 1) * 0.25; // Q1=0, Q2=0.25, Q3=0.5, Q4=0.75
            }

            // Handle single year (2023)
            return parseInt(period, 10);
        };

        return getSortablePeriod(a) - getSortablePeriod(b);
    });

    // Calculate chart dimensions
    const chartWidth = 1000; // Set to 1000px width
    const chartHeight = height - 110; // Leave space for legend and rotated labels (80 + 30)
    const padding = { top: 20, right: 20, bottom: 90, left: 60 }; // Increased bottom padding for rotated labels and legend

    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    // Calculate min and max values
    const values = data.map(d => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue;
    const paddingValue = valueRange * 0.1; // 10% padding

    const yMin = Math.max(0, minValue - paddingValue);
    const yMax = maxValue + paddingValue;
    const yRange = yMax - yMin;

    // Helper functions for coordinates
    const getX = (period: string) => {
        const index = periods.indexOf(period);
        return padding.left + (index / (periods.length - 1)) * plotWidth;
    };

    const getY = (value: number) => padding.top + plotHeight - ((value - yMin) / yRange) * plotHeight;

    // Generate SVG path for each school
    const generatePath = (school: string) => {
        const schoolData = data.filter(d => d.school === school).sort((a, b) => (
            periods.indexOf(a.period) - periods.indexOf(b.period)
        ));

        if (schoolData.length === 0) return '';

        const pathData = schoolData.map((point, index) => {
            const x = getX(point.period);
            const y = getY(point.value);
            return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
        }).join(' ');

        return pathData;
    };

    // Generate points for each school
    const generatePoints = (school: string) => {
        const schoolData = data.filter(d => d.school === school);
        return schoolData.map((point) => {
            const x = getX(point.period);
            const y = getY(point.value);
            return (
                <circle
                    key={`${school}-${point.period}`}
                    cx={x}
                    cy={y}
                    r="4"
                    fill={getSchoolColor(school)}
                    stroke="white"
                    strokeWidth="2" />
            );
        });
    };

    return (
        <div className="line-chart" style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <svg width={chartWidth} height={chartHeight + 30} style={{ border: '1px solid #f0f0f0', minWidth: '100%' }}>
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                    const y = padding.top + plotHeight - (ratio * plotHeight);
                    const value = yMin + (ratio * yRange);
                    return (
                        <g key={`grid-${ratio}`}>
                            <line
                                x1={padding.left}
                                y1={y}
                                x2={padding.left + plotWidth}
                                y2={y}
                                stroke="#f0f0f0"
                                strokeWidth="1" />
                            <text
                                x={padding.left - 10}
                                y={y + 4}
                                textAnchor="end"
                                fontSize="12"
                                fill="#666">
                                {value.toFixed(1)}
                            </text>
                        </g>
                    );
                })}

                {/* Period labels */}
                {periods.map((period) => {
                    const x = getX(period);
                    return (
                        <text
                            key={period}
                            x={x}
                            y={chartHeight - 15}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#666"
                            transform={`rotate(-65, ${x}, ${chartHeight - 15})`}>
                            {period}
                        </text>
                    );
                })}

                {/* Lines for each school */}
                {schools.map((school) => {
                    const path = generatePath(school);
                    if (!path) return null;

                    return (
                        <path
                            key={`line-${school}`}
                            d={path}
                            fill="none"
                            stroke={getSchoolColor(school)}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round" />
                    );
                })}

                {/* Points for each school */}
                {schools.map((school) => generatePoints(school))}
            </svg>

            {/* Legend */}
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {schools.map((school) => (
                    <div key={`legend-${school}`} style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                            style={{
                                width: 12,
                                height: 12,
                                backgroundColor: getSchoolColor(school),
                                marginRight: 6,
                                borderRadius: '50%',
                            }} />
                        <Text style={{ fontSize: '12px' }}>{school}</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

LineChart.defaultProps = {
    height: 300,
};

interface TimeSeriesComparisonResultsProps {
    timeSeriesComparison: TimeSeriesComparison;
}

const TimeSeriesComparisonResults: React.FC<TimeSeriesComparisonResultsProps> = ({ timeSeriesComparison }) => {
    // Get special indicators from Redux
    const { specialIndicatorsTimeSeries } = useSelector((state: CombinedStates) => state.indicator);

    // Ensure specialIndicatorsTimeSeries is always an array
    const safeSpecialIndicatorsTimeSeries = Array.isArray(specialIndicatorsTimeSeries) ? specialIndicatorsTimeSeries : [];

    // Helper function to map special indicator periods to school year format
    const mapSpecialIndicatorPeriod = (period: string): string => {
        // If period is already in school year format (e.g., "2023-2024"), return as is
        if (period.includes('-')) {
            return period;
        }

        // If period is just a year (e.g., "2023"), convert to school year format
        const year = parseInt(period, 10);
        if (!Number.isNaN(year)) {
            return `${year}-${year + 1}`;
        }

        return period;
    };

    // Helper function to format special indicator data for charts
    const formatSpecialIndicatorDataForChart = (specialIndicator: SpecialIndicatorTimeSeries) => {
        const chartData: Array<{
            period: string;
            periodDescription: string;
            school: string;
            value: number;
            displayValue: string;
            confirmed: boolean;
            updatedAt: string;
        }> = [];

        specialIndicator.time_series.forEach((dataPoint) => {
            const formattedValue = formatIndicatorValueForChart(
                parseFloat(dataPoint.value.toString()),
                specialIndicator.unit,
                true,
            );

            chartData.push({
                period: mapSpecialIndicatorPeriod(dataPoint.period),
                periodDescription: dataPoint.period_description,
                school: specialIndicator?.title || 'Švietimo skyrius/centras', // Special indicator appears as education scope
                value: formattedValue.numericValue,
                displayValue: formattedValue.displayValue,
                confirmed: dataPoint.confirmed_at !== null,
                updatedAt: dataPoint.updated_at,
            });
        });

        return chartData;
    };

    // Safety check for data
    if (!timeSeriesComparison || !timeSeriesComparison.schools || !timeSeriesComparison.indicators || !timeSeriesComparison.statistics) {
        return (
            <Card title="Laiko eilutės palyginimo rezultatai" className="results-card">
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">Nėra duomenų palyginimui</Text>
                </div>
            </Card>
        );
    }

    // Helper function to format data for line chart
    const formatDataForLineChart = (indicator: SchoolTimeSeriesIndicator) => {
        const chartData: Array<{
            period: string;
            periodDescription: string;
            school: string;
            value: number;
            displayValue: string;
            confirmed: boolean;
            updatedAt: string;
        }> = [];

        indicator.schools_data.forEach((schoolData) => {
            schoolData.time_series.forEach((dataPoint) => {
                const formattedValue = formatIndicatorValueForChart(
                    dataPoint.value,
                    indicator.unit,
                    true,
                );

                chartData.push({
                    period: dataPoint.period,
                    periodDescription: dataPoint.period_description,
                    school: schoolData.school_name,
                    value: formattedValue.numericValue,
                    displayValue: formattedValue.displayValue,
                    confirmed: dataPoint.confirmed_at !== null,
                    updatedAt: dataPoint.updated_at,
                });
            });
        });

        return chartData;
    };

    // Generate colors for schools
    const getSchoolColor = (schoolName: string) => {
        const colors = [
            // Strategically ordered for maximum visual contrast between consecutive colors
            '#1890ff', // Blue
            '#fa8c16', // Orange
            '#52c41a', // Green
            '#eb2f96', // Pink
            '#722ed1', // Purple
            '#fadb14', // Yellow
            '#13c2c2', // Cyan
            '#2f54eb', // Indigo
            '#cf1322', // Red
            '#389e0d', // Dark Green
            '#ff4d4f', // Light Red
            '#531dab', // Dark Purple
            '#ffa940', // Light Orange
            '#08979c', // Dark Cyan
            '#ff7875', // Light Pink
            '#8c8c8c', // Gray
            '#ff7a45', // Coral
            '#a0d911', // Lime
            '#ff9c6e', // Peach
            '#36cfc9', // Teal
            '#ffbb96', // Light Coral
            '#b37feb', // Light Purple
            '#ffd4b3', // Very Light Orange
            '#5cdbd3', // Light Teal
            '#ffec3d', // Bright Yellow
            '#d3f261', // Light Lime
            '#ffa39e', // Light Salmon
            '#87e8de', // Very Light Cyan
            '#ffccc7', // Very Light Pink
            '#bae637', // Yellow Green
            '#ffd591', // Light Peach
            '#73d13d', // Medium Green
            '#ffc069', // Medium Orange
            '#95de64', // Light Green
            '#ffa940', // Medium Orange
            '#b7eb8f', // Very Light Green
            '#ffd591', // Light Orange
            '#d9f7be', // Very Light Green
            '#ffe7ba', // Very Light Yellow
            '#f6ffed', // Almost White Green
            '#fff7e6', // Almost White Orange
            '#ff4d4f', // Medium Red
            '#9254de', // Medium Purple
            '#ff7875', // Light Red
            '#b37feb', // Light Purple
            '#ffa39e', // Light Pink
            '#d3adf7', // Very Light Purple
            '#ffccc7', // Very Light Pink
            '#efdbff', // Almost White Purple
            '#f9f0ff', // Almost White Purple
            '#ff7a45', // Coral
            '#a0d911', // Lime
            '#ff9c6e', // Peach
            '#bae637', // Yellow Green
            '#ffbb96', // Light Coral
            '#d3f261', // Light Lime
            '#ffd4b3', // Very Light Orange
            '#eaff8f', // Very Light Yellow
            '#ffec3d', // Bright Yellow
            '#fff566', // Very Light Yellow
            '#8c8c8c', // Medium Gray
            '#bfbfbf', // Light Gray
            '#d9d9d9', // Very Light Gray
            '#f0f0f0', // Almost White Gray
            '#595959', // Dark Gray
            '#262626', // Very Dark Gray
        ];
        const schoolNames = [...new Set(timeSeriesComparison.schools.map(s => s.name))];
        const index = schoolNames.indexOf(schoolName);
        return colors[index % colors.length] || '#1890ff';
    };

    // Generate colors for special indicators
    const getSpecialIndicatorColor = (indicatorTitle: string): string => {
        const specialColors = [
            '#ff6b6b', // Red
            '#4ecdc4', // Teal
            '#45b7d1', // Blue
            '#96ceb4', // Green
            '#feca57', // Yellow
            '#ff9ff3', // Pink
            '#54a0ff', // Blue
            '#5f27cd', // Purple
            '#00d2d3', // Cyan
            '#ff9f43', // Orange
            '#10ac84', // Dark Green
            '#ee5a24', // Red Orange
            '#0984e3', // Blue
            '#6c5ce7', // Purple
            '#a29bfe', // Light Purple
            '#fd79a8', // Pink
            '#fdcb6e', // Light Orange
            '#e17055', // Coral
            '#00b894', // Green
            '#e84393', // Pink
        ];
        // Use hash of the title to get consistent color for same indicator
        let hash = 0;
        for (let i = 0; i < indicatorTitle.length; i++) {
            const char = indicatorTitle.charCodeAt(i);
            hash = ((hash * 31) + char) % 2147483647; // Simple hash function
        }
        const index = Math.abs(hash) % specialColors.length;
        return specialColors[index] || '#ff6b6b'; // Fallback color
    };

    // Check if indicator should show line chart (skip for taip_ne and text units)
    const shouldShowLineChart = (unit: string) => unit !== 'taip_ne' && unit !== 'text';

    // Reusable function to render indicator info
    const renderIndicatorInfo = (indicator: SchoolTimeSeriesIndicator) => (
        <div className="indicator-info">
            <Text strong>R{indicator.id} - {indicator.title}</Text>
            <Text type="secondary">{' '}{indicator.unit}</Text>
        </div>
    );

    return (
        <Card title="Laiko eilutės palyginimo rezultatai" className="results-card">
            <div className="time-series-results">
                {/* Summary Statistics */}
                <div className="summary-statistics">
                    <Title level={5}>Bendri duomenys</Title>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Iš viso mokyklų"
                                    value={timeSeriesComparison.schools.length} />
                            </Card>
                        </Col>
                        <Col xs={24} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Iš viso rodiklių"
                                    value={timeSeriesComparison.indicators.length} />
                            </Card>
                        </Col>
                        <Col xs={24} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Rodikliai su duomenimis"
                                    value={timeSeriesComparison.indicators.filter(ind => ind.schools_data.some(school => school.time_series.length > 0)).length} />
                            </Card>
                        </Col>
                        <Col xs={24} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Bendras duomenų taškų skaičius"
                                    value={timeSeriesComparison.indicators.reduce((total, ind) => total + ind.schools_data.reduce((schoolTotal, school) => schoolTotal + school.time_series.length, 0), 0)} />
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* School Statistics */}
                <div className="school-statistics" style={{ marginTop: 24 }}>
                    <Title level={5}>Mokyklų statistika</Title>
                    <Row gutter={[16, 16]}>
                        {timeSeriesComparison.statistics.map((schoolStat) => (
                            <Col xs={24} md={8} key={schoolStat.school_id}>
                                <Card size="small" title={schoolStat.school_name}>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong>Duomenų užpildymas:</Text>
                                        <Text style={{ marginLeft: 8 }}>
                                            {schoolStat.overall_statistics?.data_completion_rate || 0}%
                                        </Text>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong>Rodikliai su duomenimis:</Text>
                                        <Text style={{ marginLeft: 8 }}>
                                            {schoolStat.overall_statistics?.indicators_with_data || 0}/
                                            {schoolStat.overall_statistics?.total_indicators || 0}
                                        </Text>
                                    </div>
                                    <div>
                                        <Text strong>Vidutinė reikšmė:</Text>
                                        <Text style={{ marginLeft: 8 }}>
                                            {schoolStat.overall_statistics?.overall_statistics?.average?.toFixed(2) || 'N/A'}
                                        </Text>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* Detailed Time Series Charts */}
                <div className="time-series-charts" style={{ marginTop: 24 }}>
                    <Title level={5}>Detalūs laiko eilutės grafikai</Title>
                    <div className="indicators-time-series">
                        {timeSeriesComparison.indicators.map((indicator) => {
                            const chartData = formatDataForLineChart(indicator);

                            // Show all special indicators for every regular indicator
                            const matchingSpecialIndicators = safeSpecialIndicatorsTimeSeries;

                            // Add special indicator data to chart data
                            let combinedChartData = [...chartData];
                            matchingSpecialIndicators.forEach(specialIndicator => {
                                const specialData = formatSpecialIndicatorDataForChart(specialIndicator);
                                // Only add special indicator data if it has time series data
                                if (specialData.length > 0) {
                                    combinedChartData = [...combinedChartData, ...specialData];
                                }
                            });

                            if (combinedChartData.length === 0) {
                                return (
                                    <div key={indicator.id} style={{ marginBottom: 32 }}>
                                        <Card size="small" className="indicator-time-series-card">
                                            {/* Indicator Title - moved to top */}
                                            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                                                {renderIndicatorInfo(indicator)}
                                            </div>

                                            {/* No data message */}
                                            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                                <Text type="secondary">Nėra duomenų šiam rodikliui</Text>
                                            </div>
                                        </Card>
                                    </div>
                                );
                            }

                            if (!shouldShowLineChart(indicator.unit)) {
                                return (
                                    <div key={indicator.id} style={{ marginBottom: 32 }}>
                                        <Card size="small" className="indicator-time-series-card">
                                            {/* Indicator Title - moved to top */}
                                            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                                                {renderIndicatorInfo(indicator)}
                                            </div>

                                            {/* Text data */}
                                            <div className="text-time-series">
                                                {indicator.schools_data.map((schoolData) => (
                                                    <div key={schoolData.school_id} style={{ marginBottom: 16 }}>
                                                        <Text strong>{schoolData.school_name}:</Text>
                                                        {schoolData.time_series.map((dataPoint) => (
                                                            <div key={`${schoolData.school_id}-${dataPoint.period_description}`} style={{ marginLeft: 16, marginTop: 4 }}>
                                                                <Text>
                                                                    {dataPoint.period_description}: {dataPoint.value}
                                                                </Text>
                                                                {dataPoint.confirmed_at && (
                                                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                                                        (patvirtinta)
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </Card>
                                    </div>
                                );
                            }

                            // Get unique schools for this indicator (including special indicators)
                            const schools = [...new Set(combinedChartData.map(d => d.school))];

                            return (
                                <div key={indicator.id} style={{ marginBottom: 32 }}>
                                    <Card size="small" className="indicator-time-series-card">
                                        {/* Indicator Title - moved to top */}
                                        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                                            {renderIndicatorInfo(indicator)}
                                        </div>

                                        {/* Chart */}
                                        <Row gutter={16}>
                                            <Col xs={24} md={24}>
                                                <div className="time-series-chart">
                                                    <LineChart
                                                        data={combinedChartData}
                                                        schools={schools}
                                                        getSchoolColor={(schoolName) => {
                                                            // Check if it's a special indicator by title
                                                            const isSpecialIndicator = specialIndicatorsTimeSeries.some(special => special.title === schoolName);
                                                            return isSpecialIndicator
                                                                ? getSpecialIndicatorColor(schoolName)
                                                                : getSchoolColor(schoolName);
                                                        }}
                                                        height={450} />
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* School Statistics */}
                                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                                            <Title level={5} style={{ marginBottom: 12 }}>Mokyklų statistika</Title>
                                            {indicator.schools_data.map((schoolData) => (
                                                <div key={schoolData.school_id} style={{ marginBottom: 8, display: 'inline-block', padding: '16px' }}>
                                                    <Text strong style={{ color: getSchoolColor(schoolData.school_name) }}>
                                                        {schoolData.school_name}:
                                                    </Text>
                                                    <div style={{ marginLeft: 8, fontSize: '12px' }}>
                                                        <div>Duomenų taškų: {schoolData.statistics?.count || 0}</div>
                                                        <div>Vidurkis: {schoolData.statistics?.average?.toFixed(2) || 'N/A'}</div>
                                                        <div>Min: {schoolData.statistics?.min || 'N/A'}</div>
                                                        <div>Max: {schoolData.statistics?.max || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Special Indicators Statistics */}
                                            {matchingSpecialIndicators
                                                .filter(specialIndicator => {
                                                    const specialData = formatSpecialIndicatorDataForChart(specialIndicator);
                                                    return specialData.length > 0;
                                                })
                                                .map((specialIndicator) => (
                                                    <div key={`special-${specialIndicator.id}`} style={{ marginBottom: 8, display: 'inline-block', padding: '16px' }}>
                                                        <Text strong style={{ color: getSpecialIndicatorColor(specialIndicator.title) }}>
                                                            {specialIndicator.title}:
                                                        </Text>
                                                        <div style={{ marginLeft: 8, fontSize: '12px' }}>
                                                            <div>Duomenų taškų: {specialIndicator.statistics?.count || 0}</div>
                                                            <div>Vidurkis: {specialIndicator.statistics?.average?.toFixed(2) || 'N/A'}</div>
                                                            <div>Min: {specialIndicator.statistics?.min || 'N/A'}</div>
                                                            <div>Max: {specialIndicator.statistics?.max || 'N/A'}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </Card>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default TimeSeriesComparisonResults;
